import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, ArrowRight, Beaker } from 'lucide-react';
import { Link } from 'react-router-dom';

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: '/forever',
    desc: 'For individual researchers exploring pharmacometrics',
    features: ['NCA Agent', '5 projects', '10 messages/day', 'Basic plots', 'Community support'],
    cta: 'Start Free',
    highlight: false,
  },
  {
    name: 'Starter',
    price: '$49',
    period: '/month',
    desc: 'For pharmacometricians who need full analysis capabilities',
    features: ['All Free features', 'PopPK + Simulator agents', 'Unlimited messages', 'Advanced plots', 'Template library', 'Email support'],
    cta: 'Start Trial',
    highlight: false,
  },
  {
    name: 'Pro',
    price: '$99',
    period: '/month',
    desc: 'For professionals producing regulatory-grade analyses',
    features: ['All Starter features', 'All 13 agents', 'Report Builder', 'Regulatory guidance search', 'BYOK (bring your own key)', 'Priority support'],
    cta: 'Start Trial',
    highlight: true,
  },
  {
    name: 'Team',
    price: '$199',
    period: '/seat/month',
    desc: 'For teams collaborating on clinical programs',
    features: ['All Pro features', 'Canvas editor', 'PBPK agent', 'Team sharing', 'Admin controls', 'Dedicated support'],
    cta: 'Contact Sales',
    highlight: false,
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  return (
    <div className="">

      <section className="pt-28 pb-20 px-6">
        <div className="max-w-[1200px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-[-0.02em]" style={{ fontFamily: 'Space Grotesk' }}>Simple, Transparent Pricing</h1>
            <p className="text-muted-foreground mt-4 max-w-lg mx-auto">Start free, upgrade as your needs grow. All plans include core AI capabilities.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className={`h-full card-hover ${plan.highlight ? 'border-primary' : ''}`}>
                  <CardHeader className="pb-4">
                    {plan.highlight && (
                      <span className="text-[10px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full w-fit mb-2">Most Popular</span>
                    )}
                    <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>{plan.name}</CardTitle>
                    <div className="flex items-baseline gap-1 mt-2">
                      <span className="text-3xl font-bold font-mono">{plan.price}</span>
                      <span className="text-sm text-muted-foreground">{plan.period}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{plan.desc}</p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2.5 mb-6">
                      {plan.features.map((f, fi) => (
                        <li key={fi} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-primary flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => navigate('/signup')}
                      className={`w-full rounded-full h-10 text-sm active:scale-[0.98] transition-transform ${
                        plan.highlight ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                      }`}
                    >
                      {plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
